/**
 * SW-aware hard refresh. Uses only `navigator.serviceWorker` + `caches` so the
 * routine is identical across CRA (SW unregistered) and the three Vite PWAs
 * (`vite-plugin-pwa` `autoUpdate`). A plain `location.reload()` would be served
 * the SW-cached old shell on the PWAs — this handshake is what actually lands
 * the user on the new build.
 *
 * INSTALL-AWARE (2026-09-02 incident): the previous version reloaded on a flat
 * 2-second fallback. The technician PWA's precache is hundreds of files, so a
 * new SW takes MINUTES to install on field connections — and every 2s reload
 * landed back on the old SW-cached shell, re-showing the forced-update modal in
 * a loop that read as "the app is bricked". This version watches the installing
 * worker's lifecycle and reloads only when that actually advances the user:
 *
 *   - reload when the new worker takes over (controllerchange), or is active
 *     and simply hasn't claimed the page;
 *   - reload on the short fallback ONLY when the update check RESOLVED and
 *     found nothing installing (either the newest SW already self-activated —
 *     reload lands on the new build — or there is nothing newer; neither can
 *     loop-thrash);
 *   - report `failed` — no reload — when the install is rejected, the check's
 *     network fetch rejects, or nothing has been discovered after a stall
 *     timeout. Reloading on a failing network IS the incident loop, so failure
 *     re-arms the UI for an explicit retry instead.
 *
 * The whole run is a module-level singleton: a second call while one is in
 * flight joins it (its onPhase is attached) instead of racing a parallel set
 * of listeners and timers against the first.
 */

import { runFlushHandlers } from '../lib/offline/flushHandlers';
import type { ApplyUpdateOptions, ApplyUpdatePhase } from './types';

/** Reload delay when the check RESOLVED and found no new worker. */
const NO_NEW_WORKER_RELOAD_MS = 2000;
/** Nudge a stuck-waiting worker (prompt-mode SWs) with SKIP_WAITING, then give
 * activation this long before reloading anyway — once a worker is `installed`
 * or beyond, a reload is served by the ACTIVE worker and cannot loop. */
const ACTIVATION_NUDGE_MS = 4000;
/** If the update check neither resolves, rejects, nor discovers a worker in
 * this long (hung SW-script fetch on captive-portal / half-open LTE), report
 * failure so the user is not pinned on "Checking..." indefinitely. */
const CHECK_STALL_MS = 20 * 1000;
/** Absolute cap on a run. Expiring means the install is genuinely wedged, not
 * merely slow — surface `failed` (retry button) rather than a surprise reload
 * that could destroy in-progress form work from the SOFT banner path. */
const TERMINAL_FAIL_MS = 5 * 60 * 1000;

async function clearAllCaches(): Promise<void> {
  if (typeof caches === 'undefined') return;
  try {
    const keys = await caches.keys();
    await Promise.all(keys.map((k) => caches.delete(k)));
  } catch {
    /* best-effort; never block the reload */
  }
}

/** How long in-flight edit flushes may delay the reload before it proceeds. */
const FLUSH_CAP_MS = 3000;

// Once a reload has started, every other route (a second controllerchange
// listener, a retry, the tech PWA's onNeedReload) must stand down — a duplicate
// would run the flush handlers concurrently and reload twice.
//
// Deliberately NEVER reset. After `location.reload()` this document is going
// away, so there is nothing to reset for. The one case it survives is a
// beforeunload handler (e.g. an unsaved-changes prompt) letting the user CANCEL
// the navigation: then further update reloads in this page lifetime are no-ops.
// That is the intended outcome — the user chose to stay, and the next launch or
// navigation loads the new build.
let reloadTriggered = false;

/**
 * THE reload for the whole update flow (#4741): flush pending in-flight edits
 * (survey/FSR drafts) to durable storage, then reload. Capped so a hung flush
 * handler can never block the reload. Idempotent.
 *
 * It is NEVER deferred behind an open form fill, by design:
 *  - a user-tapped update is explicit consent, and deferring it behind the
 *    non-dismissible modal that covers the form is a lockout (the fill it waits
 *    on cannot close);
 *  - once a new service worker controls the page, deferring is WORSE than
 *    reloading: cleanupOutdatedCaches has dropped the old precache and Hosting
 *    no longer serves the old hashed chunks, so the next lazy chunk the form
 *    needs fails and the page crashes, losing in-memory photos anyway.
 * The form-fill protection therefore lives BEFORE the takeover: no update UI
 * and no automatic update check while a fill holds updates (see
 * shouldHoldUpdatesForFormFill in formFillGuard).
 *
 * Exported for the technician PWA, whose vite-plugin-pwa autoUpdate register
 * script would otherwise bare-reload on workbox 'activated' (see
 * swRegisterGuards.ts).
 */
export function reloadAfterFlush(): void {
  if (reloadTriggered) return;
  reloadTriggered = true;
  const cap = new Promise<void>((r) => {
    window.setTimeout(r, FLUSH_CAP_MS);
  });
  void Promise.race([runFlushHandlers(), cap]).finally(() => {
    // Bust any bfcache and force a fresh document fetch.
    window.location.reload();
  });
}

// Singleton run state — see the header comment.
let activeRun: Promise<ApplyUpdatePhase> | null = null;
let currentPhase: ApplyUpdatePhase | null = null;
const activeReporters = new Set<(phase: ApplyUpdatePhase) => void>();
// After a failed run, the page may still be taken over later by a background
// update succeeding where ours failed. Running old-build JS under the new SW
// serves 404s for every old lazy chunk, so arm ONE persistent reload-on-
// takeover listener the first time a run fails. (The takeover has already
// happened when it fires — reload immediately, flushed; see reloadAfterFlush.)
let failedTakeoverArmed = false;

function report(phase: ApplyUpdatePhase): void {
  currentPhase = phase;
  activeReporters.forEach((fn) => {
    try {
      fn(phase);
    } catch {
      /* a UI callback must never break the update itself */
    }
  });
}

export function applyUpdate(
  options: ApplyUpdateOptions = {},
): Promise<ApplyUpdatePhase> {
  if (options.onPhase) {
    activeReporters.add(options.onPhase);
    // A joiner (second surface, or a re-mounted component) catches up on the
    // phase the in-flight run already reached.
    if (activeRun && currentPhase) {
      try {
        options.onPhase(currentPhase);
      } catch {
        /* see report() */
      }
    }
  }
  if (activeRun) return activeRun;
  activeRun = runApplyUpdate().finally(() => {
    activeRun = null;
    currentPhase = null;
    activeReporters.clear();
  });
  return activeRun;
}

async function runApplyUpdate(): Promise<ApplyUpdatePhase> {
  const swContainer =
    typeof navigator !== 'undefined' ? navigator.serviceWorker : undefined;

  if (!swContainer) {
    // CRA path: no service worker manages caches, so clearing any stray Cache
    // Storage entries + a hard reload is the correct way to bypass a stale shell.
    report('reloading');
    await clearAllCaches();
    reloadAfterFlush();
    return 'reloading';
  }

  report('checking');

  try {
    const reg = await swContainer.getRegistration();

    if (!reg) {
      // No registration handle — treat like the no-SW path.
      report('reloading');
      await clearAllCaches();
      reloadAfterFlush();
      return 'reloading';
    }

    return await new Promise<ApplyUpdatePhase>((resolve) => {
      let settled = false;
      const timers: number[] = [];
      const workerListeners: Array<[ServiceWorker, () => void]> = [];
      const later = (fn: () => void, ms: number) => {
        timers.push(window.setTimeout(fn, ms));
      };

      const finish = (outcome: ApplyUpdatePhase) => {
        if (settled) return;
        settled = true;
        timers.forEach((t) => window.clearTimeout(t));
        swContainer.removeEventListener('controllerchange', onControllerChange);
        reg.removeEventListener('updatefound', onUpdateFound);
        workerListeners.forEach(([w, fn]) =>
          w.removeEventListener('statechange', fn),
        );
        if (outcome === 'failed' && !failedTakeoverArmed) {
          failedTakeoverArmed = true;
          swContainer.addEventListener('controllerchange', reloadAfterFlush, {
            once: true,
          });
        }
        report(outcome);
        if (outcome === 'reloading') reloadAfterFlush();
        resolve(outcome);
      };

      // Primary success signal: the new SW has activated AND claimed this page.
      const onControllerChange = () => finish('reloading');
      swContainer.addEventListener('controllerchange', onControllerChange);

      // SW-managed apps (vite-plugin-pwa/Workbox): do NOT blanket-clear Cache
      // Storage — that would wipe the NEW service worker's just-installed
      // precache and strand an offline field client. Workbox's own
      // cleanupOutdatedCaches removes stale precaches on activate. We only
      // drive the SW takeover + reload.

      // Workers already being watched (or already dead) must never be adopted
      // again — re-adopting a `redundant` worker that the registration still
      // references would recurse forever.
      const watched = new Set<ServiceWorker>();

      // One activation grace period per run, armed when a worker first leaves
      // `installing` — not re-armed per state, so the deadline is legible.
      let nudgeArmed = false;
      const armActivationNudge = () => {
        if (nudgeArmed) return;
        nudgeArmed = true;
        later(() => finish('reloading'), ACTIVATION_NUDGE_MS);
      };

      const watchWorker = (worker: ServiceWorker) => {
        if (watched.has(worker)) return;
        watched.add(worker);
        const phaseFor = (state: string): void => {
          if (settled) return;
          if (state === 'installing') {
            report('downloading');
          } else if (state === 'installed') {
            // Installed but not yet active. autoUpdate SWs self-skipWaiting;
            // nudge prompt-mode SWs (THIS worker, not whatever reg.waiting
            // points at by now), then reload after a grace period — a reload
            // once a worker is installed-or-beyond is served by the ACTIVE
            // worker and cannot loop.
            report('activating');
            worker.postMessage({ type: 'SKIP_WAITING' });
            armActivationNudge();
          } else if (state === 'activating' || state === 'activated') {
            // Active. If clientsClaim doesn't fire controllerchange promptly,
            // reload anyway — the navigation will be served by the new worker.
            report('activating');
            armActivationNudge();
          } else if (state === 'redundant') {
            // Install/activation FAILED (a precache fetch died, script error…)
            // — unless a NEWER worker replaced this one mid-flight, in which
            // case adopt the replacement. Reloading on failure would land on
            // the old shell and re-show the modal — an infinite loop on a bad
            // connection. Surface the failure instead so the UI can offer an
            // explicit retry.
            if (!adoptExisting()) finish('failed');
          }
        };
        const onStateChange = () => phaseFor(worker.state);
        workerListeners.push([worker, onStateChange]);
        worker.addEventListener('statechange', onStateChange);
        phaseFor(worker.state);
      };

      // A worker may already be mid-flight from an earlier background check
      // (e.g. the PWAs' foreground update pings) — adopt it rather than racing.
      const adoptExisting = (): boolean => {
        const inFlight = reg.installing ?? reg.waiting;
        if (inFlight && !watched.has(inFlight) && inFlight.state !== 'redundant') {
          watchWorker(inFlight);
          return true;
        }
        return false;
      };

      const onUpdateFound = () => {
        if (reg.installing) watchWorker(reg.installing);
      };
      reg.addEventListener('updatefound', onUpdateFound);

      // Stall guard: a hung update() (unsettled fetch) with nothing discovered
      // must not pin the UI on "Checking..." — fail so the user can retry.
      later(() => {
        if (!settled && watched.size === 0 && !adoptExisting()) {
          finish('failed');
        }
      }, CHECK_STALL_MS);

      // Absolute cap so no state — however unexpected — strands the user
      // without an actionable control.
      later(() => finish('failed'), TERMINAL_FAIL_MS);

      const adopted = adoptExisting();

      // Pull the newest SW from the network.
      reg.update().then(
        () => {
          if (settled) return;
          // Short-circuit on the already-adopted flag FIRST — adoptExisting()
          // has side effects, and adopting a second worker here would let the
          // first one's `redundant` transition read as a false failure.
          if (adopted || adoptExisting()) return;
          // Check RESOLVED with no new worker: give `updatefound` a brief
          // grace window (update() can resolve before the worker object
          // appears), then reload — either the newest SW already
          // self-activated and the reload lands on the new build, or there is
          // nothing newer. If a worker WAS adopted in the meantime, this
          // fallback stands down and the worker's lifecycle drives the
          // outcome.
          later(() => {
            if (!settled && watched.size === 0 && !adoptExisting()) {
              finish('reloading');
            }
          }, NO_NEW_WORKER_RELOAD_MS);
        },
        () => {
          // Check REJECTED (network failure fetching the SW script). If an
          // install is already in flight, let it drive; otherwise reloading
          // would serve the old shell and re-open the modal — the incident
          // loop. Fail so the UI re-arms for an explicit retry.
          if (!settled && watched.size === 0 && !adoptExisting()) {
            finish('failed');
          }
        },
      );
    });
  } catch {
    // Any unexpected failure: still get the user onto a fresh document.
    report('reloading');
    reloadAfterFlush();
    return 'reloading';
  }
}
